/**
 * Effective Age Helper
 *
 * Computes effective age by rolling forward from capture date.
 * No DOB is stored or required.
 *
 * Formula: effectiveAge = age + floor((now - ageCapturedAt) / 365.25 days)
 * Clamped to 13–100. Falls back to raw age on any error.
 */

const MILLIS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
const MIN_AGE = 13;
const MAX_AGE = 100;

/**
 * Convert ageCapturedAt to millis, handling Firestore Timestamp or number.
 */
function toMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof (value as any).toMillis === 'function') {
    return (value as any).toMillis();
  }
  if (typeof value === 'number' && value > 0) {
    return value;
  }
  return null;
}

/**
 * Get the effective age in whole years, rolling forward from capture date.
 *
 * @param age - The age entered by the user at onboarding
 * @param ageCapturedAt - Timestamp or millis when age was entered
 * @returns Effective age, or null if age is missing
 */
export function getEffectiveAgeYears(
  age: number | undefined,
  ageCapturedAt: unknown,
): number | null {
  if (age == null) return null;

  const capturedMillis = toMillis(ageCapturedAt);
  if (capturedMillis == null) {
    // No capture date — backward compat, return raw age
    return age;
  }

  const nowMillis = Date.now();
  const deltaMillis = nowMillis - capturedMillis;

  // Future capture date (bad data) — clamp to 0
  const yearsDelta = Math.max(0, Math.floor(deltaMillis / MILLIS_PER_YEAR));
  const effectiveAge = age + yearsDelta;

  // Sanity clamp
  if (effectiveAge < MIN_AGE || effectiveAge > MAX_AGE) {
    console.warn(
      `[getEffectiveAgeYears] Out of range: effectiveAge=${effectiveAge}, raw age=${age}, yearsDelta=${yearsDelta}. Returning raw age.`,
    );
    return age;
  }

  return effectiveAge;
}

/**
 * Compute age in whole years from a date of birth string.
 *
 * @param dateOfBirth - YYYY-MM-DD string
 * @returns Age in years, or null if DOB is invalid/unparseable
 */
export function computeAgeFromDOB(dateOfBirth: string | undefined): number | null {
  if (!dateOfBirth) return null;

  const match = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const birthYear = parseInt(match[1], 10);
  const birthMonth = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const birthDay = parseInt(match[3], 10);

  const now = new Date();
  let age = now.getFullYear() - birthYear;

  // Adjust if birthday hasn't occurred yet this year
  const monthNow = now.getMonth();
  const dayNow = now.getDate();
  if (monthNow < birthMonth || (monthNow === birthMonth && dayNow < birthDay)) {
    age--;
  }

  // Sanity clamp
  if (age < MIN_AGE || age > MAX_AGE) {
    return null;
  }

  return age;
}
